import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { submitQuote, fetchCapabilities } from "@/lib/api";
import {
  CheckCircle,
  PaperPlaneTilt,
  ChatCircleText,
} from "@phosphor-icons/react";

export default function QuoteDialog({
  open,
  onOpenChange,
  flake,
  previewBase64,
}) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    sms_opt_in: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [smsAvailable, setSmsAvailable] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchCapabilities()
      .then((c) => setSmsAvailable(!!c.sms_available))
      .catch(() => setSmsAvailable(false));
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => {
    setForm({ name: "", email: "", phone: "", message: "", sms_opt_in: true });
    setDone(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Name, email and phone are required.");
      return;
    }
    setSubmitting(true);
    try {
      await submitQuote({
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message,
        flake_id: flake?.id || null,
        preview_image_base64: previewBase64 || null,
        sms_opt_in: !!form.sms_opt_in,
      });
      setDone(true);
      toast.success("Quote request sent.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not submit. Try again.";
      toast.error(typeof msg === "string" ? msg : "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 200);
      }}
    >
      <DialogContent
        className="sm:max-w-[520px] border"
        style={{
          background: "var(--epx-surface)",
          borderColor: "var(--epx-border)",
          color: "white",
        }}
        data-testid="quote-dialog"
      >
        {!done && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl">
                Request a free quote
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                We&apos;ll get back to you within one business day.
              </DialogDescription>
            </DialogHeader>

            <form
              onSubmit={onSubmit}
              className="space-y-4 mt-2"
              data-testid="quote-form"
            >
              {previewBase64 && (
                <div className="flex items-center gap-3 p-3 rounded-lg border bg-black/30"
                     style={{ borderColor: "var(--epx-border)" }}>
                  <img
                    src={previewBase64}
                    alt="preview"
                    className="w-14 h-14 rounded-md object-cover border border-white/10"
                  />
                  <div className="min-w-0">
                    <p className="text-xs text-zinc-400">Your preview</p>
                    <p className="text-sm font-medium truncate">
                      {flake ? `${flake.name} flake blend` : "Custom preview"}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="q-name" className="text-zinc-300">
                    Name
                  </Label>
                  <Input
                    id="q-name"
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    placeholder="Jane Smith"
                    className="bg-black/30 border-white/10 text-white placeholder:text-zinc-500"
                    data-testid="quote-name"
                  />
                </div>
                <div>
                  <Label htmlFor="q-phone" className="text-zinc-300">
                    Phone
                  </Label>
                  <Input
                    id="q-phone"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    placeholder="(555) 123-4567"
                    className="bg-black/30 border-white/10 text-white placeholder:text-zinc-500"
                    data-testid="quote-phone"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="q-email" className="text-zinc-300">
                  Email
                </Label>
                <Input
                  id="q-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="jane@example.com"
                  className="bg-black/30 border-white/10 text-white placeholder:text-zinc-500"
                  data-testid="quote-email"
                />
              </div>

              <div>
                <Label htmlFor="q-msg" className="text-zinc-300">
                  Project details (optional)
                </Label>
                <Textarea
                  id="q-msg"
                  rows={3}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  placeholder="Approx square footage, timeline, condition of the floor…"
                  className="bg-black/30 border-white/10 text-white placeholder:text-zinc-500"
                  data-testid="quote-message"
                />
              </div>

              {smsAvailable && (
                <label
                  className="flex items-start gap-3 p-3 rounded-lg border border-white/10 bg-black/20 cursor-pointer hover:bg-black/30 transition-colors"
                  data-testid="sms-opt-in-row"
                >
                  <input
                    type="checkbox"
                    checked={form.sms_opt_in}
                    onChange={(e) => set("sms_opt_in", e.target.checked)}
                    className="mt-0.5 h-4 w-4 accent-green-500"
                    data-testid="sms-opt-in-checkbox"
                  />
                  <div>
                    <p className="text-sm font-medium text-white flex items-center gap-1.5">
                      <ChatCircleText size={14} weight="fill" style={{ color: "var(--epx-primary)" }} />
                      Text me my floor preview
                    </p>
                    <p className="text-xs text-zinc-400 mt-0.5">
                      We&apos;ll send your AI preview straight to your phone so
                      you can show family or save it for later.
                    </p>
                  </div>
                </label>
              )}

              <DialogFooter className="pt-1">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 font-semibold text-white"
                  style={{
                    background: "var(--epx-primary)",
                    boxShadow: "0 0 24px rgba(var(--epx-primary-rgb),0.30)",
                  }}
                  data-testid="quote-submit-button"
                >
                  <PaperPlaneTilt size={16} weight="fill" className="mr-2" />
                  {submitting ? "Sending…" : "Send my quote request"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}

        {done && (
          <div
            className="py-6 flex flex-col items-center text-center"
            data-testid="quote-success"
          >
            <CheckCircle
              size={48}
              weight="duotone"
              style={{ color: "var(--epx-primary)" }}
            />
            <h3 className="font-heading text-2xl font-semibold mt-3">
              Quote request sent
            </h3>
            <p className="text-zinc-400 mt-2 max-w-sm">
              Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""}! We
              received your request and will be in touch shortly.
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              className="mt-6 h-11 px-6 font-semibold"
              style={{ background: "var(--epx-primary)", color: "white" }}
              data-testid="quote-close-success"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
